import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { StringField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function SentenceJumblesExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResults = false,
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.sentenceJumblesQuestions)
    ? exercise.sentenceJumblesQuestions.map((q) => ({
        correctSentence: q.correctSentence?.trim() || "",
        jumbledWords: Array.isArray(q.jumbledWords)
          ? q.jumbledWords.map((w) => String(w).trim())
          : [],
        requireCapitalization: Boolean(q.requireCapitalization),
      }))
    : [];

  /* ================= STATE ================= */
  const [state, setState] = useState({
    index: 0,
    answers: {}, // { [qIndex]: number[] }
    hasInteracted: false,
  });

  const completedRef = useRef(false);
  const current = questions[state.index];

  /* ================= RESET ON RETRY ================= */
  useEffect(() => {
    if (!exercise?._id) return;

    setState({
      index: 0,
      answers: {},
      hasInteracted: false,
    });

    completedRef.current = false;
  }, [exercise._id]);

  useEffect(() => {
    if (!showResults) completedRef.current = false;
  }, [showResults]);

  /* ================= SHUFFLE ================= */
  const [shuffled, setShuffled] = useState([]);

  useEffect(() => {
    if (!current) return;

    const shuffledWords = current.jumbledWords
      .map((word, idx) => ({ word, id: idx }))
      .sort(() => Math.random() - 0.5);

    setShuffled(shuffledWords);
  }, [state.index, questions]);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "", cap = false) => {
    let t = String(text).trim();
    if (!cap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "").replace(/\s+/g, " ");
  };

  const userOrder = state.answers[state.index] || [];

  /* ================= HANDLE DROP ================= */
  const handleDrop = (e) => {
    if (!isPreview || showResults) return;
    e.preventDefault();

    const id = Number(e.dataTransfer.getData("text/plain"));
    if (userOrder.includes(id)) return;

    setState((prev) => {
      const nextOrder = [...userOrder, id];

      const nextAnswers = {
        ...prev.answers,
        [state.index]: nextOrder,
      };

      const nextState = {
        ...prev,
        answers: nextAnswers,
        hasInteracted: true,
      };

      setAttempt(nextState);

      /* 🔥 IMPORTANT: ALWAYS update parent */
      onComplete(nextAnswers, false);

      /* ✅ CHECK FINAL RESULT */
      const allAnswered =
        Object.keys(nextAnswers).length === questions.length &&
        Object.values(nextAnswers).every(
          (arr) => arr.length === questions[0].jumbledWords.length
        );

      if (allAnswered && !completedRef.current) {
        completedRef.current = true;

        const allCorrect = questions.every((q, idx) => {
          const order = nextAnswers[idx] || [];
          const built = order.map((i) => q.jumbledWords[i]).join(" ");

          return (
            normalize(built, q.requireCapitalization) ===
            normalize(q.correctSentence, q.requireCapitalization)
          );
        });

        onComplete(nextAnswers, allCorrect);
      }

      return nextState;
    });
  };

  const handleDragStart = (e, id) => {
    if (!isPreview) return;
    e.dataTransfer.setData("text/plain", String(id));
  };

  const goNext = () => {
    if (state.index < questions.length - 1) {
      setState((p) => ({ ...p, index: p.index + 1 }));
    }
  };

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="p-6 space-y-6">
            <StringField
              label={`Correct Sentence ${qIdx + 1}`}
              value={q.correctSentence}
              onChange={(v) => {
                const next = [...questions];
                next[qIdx].correctSentence = v;
                onUpdate("sentenceJumblesQuestions", next);
              }}
            />

            <div className="space-y-3">
              {q.jumbledWords.map((w, wIdx) => (
                <div key={wIdx} className="flex gap-2">
                  <StringField
                    value={w}
                    onChange={(v) => {
                      const next = [...questions];
                      next[qIdx].jumbledWords[wIdx] = v;
                      onUpdate("sentenceJumblesQuestions", next);
                    }}
                  />
                </div>
              ))}
            </div>

            <CapitalizationRule
              checked={q.requireCapitalization}
              onChange={(c) => {
                const next = [...questions];
                next[qIdx].requireCapitalization = c;
                onUpdate("sentenceJumblesQuestions", next);
              }}
            />
          </Card>
        ))}
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!current) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>No questions</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= BUILD SENTENCE ================= */
  const builtSentence = userOrder
    .map((i) => current.jumbledWords[i])
    .join(" ");

  const isComplete =
    userOrder.length === current.jumbledWords.length;

  const isCorrect =
    isComplete &&
    normalize(builtSentence, current.requireCapitalization) ===
      normalize(current.correctSentence, current.requireCapitalization);

  /* ================= UI ================= */
  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle>Sentence Jumbles</CardTitle>
        <CardDescription>
          Question {state.index + 1} / {questions.length}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-8">
        {/* WORD BANK */}
        <div className="flex flex-wrap gap-3 justify-center">
          {shuffled.map(({ word, id }) => (
            <Button
              key={id}
              draggable={!showResults}
              onDragStart={(e) => handleDragStart(e, id)}
              disabled={userOrder.includes(id) || showResults}
              variant="secondary"
            >
              {word}
            </Button>
          ))}
        </div>

        {/* DROP ZONE */}
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className={cn(
            "min-h-24 border-2 border-dashed rounded-xl p-6 text-center",
            showResults && isCorrect && "bg-green-100 border-green-400",
            showResults && !isCorrect && "bg-red-100 border-red-400"
          )}
        >
          {builtSentence || "Drag words here"}
        </div>

        {/* RESULT */}
        {showResults && isComplete && (
          <div className="text-center">
            {isCorrect ? (
              <p className="text-green-600 font-bold">Correct</p>
            ) : (
              <p className="text-red-600 font-bold">
                Correct: {current.correctSentence}
              </p>
            )}
          </div>
        )}

        {/* NEXT */}
        {!showResults && isComplete && state.index < questions.length - 1 && (
          <div className="text-center">
            <Button onClick={goNext}>Next</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}